import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { intelligentSearchService } from '../../services/IntelligentSearchService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';

const IntelligentSearchHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'searching', 'cancelled'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadSearchHistory();
  }, [currentPage, filter, sortBy, sortOrder]);

  const loadSearchHistory = async () => {
    setLoading(true);
    
    const params = {
      page: currentPage,
      page_size: pageSize,
      status: filter !== 'all' ? filter : undefined,
    };

    const { data, error } = await intelligentSearchService.getIntelligentSearchSessions(params);
    
    if (data) {
      setSessions(data.sessions || []);
      setTotalCount(data.total || 0);
    } else if (error) {
      console.error('Error loading search history:', error);
      toast.error('Failed to load search history');
    }
    
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'circles_generated': 'blue',
      'user_reviewing': 'yellow',
      'searching': 'purple',
      'completed': 'green',
      'cancelled': 'red'
    };
    
    const color = colors[status] || 'gray';
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>
        {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
      return `${(seconds / 60).toFixed(1)}m`;
    } else {
      return `${(seconds / 3600).toFixed(1)}h`;
    }
  };

  const calculateEfficiency = (session) => {
    if (!session.api_requests_made || !session.total_places_found) return 0;
    return (session.total_places_found / session.api_requests_made).toFixed(1);
  };

  const filteredAndSortedSessions = [...sessions].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'area_name':
        comparison = (a.search_area?.area_name || '').localeCompare(b.search_area?.area_name || '');
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'total_places_found':
        comparison = (a.total_places_found || 0) - (b.total_places_found || 0);
        break;
      case 'api_requests_made':
        comparison = (a.api_requests_made || 0) - (b.api_requests_made || 0);
        break;
      case 'created_at':
      default:
        comparison = new Date(a.created_at) - new Date(b.created_at);
        break;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && sessions.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Search History</h1>
              <p className="text-sm text-gray-600">View your intelligent search sessions and performance</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/intelligent-search')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                New Search
              </button>
              <button
                onClick={() => navigate('/intelligent-search/dashboard')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              {/* Filter by Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
                <select
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="searching">Searching</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Sort Controls */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                <div className="flex space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="created_at">Date</option>
                    <option value="area_name">Area Name</option>
                    <option value="status">Status</option>
                    <option value="total_places_found">Places Found</option>
                    <option value="api_requests_made">API Requests</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div>
                <span className="font-medium">Total Sessions:</span> {totalCount}
              </div>
              <div>
                <span className="font-medium">Completed:</span> {sessions.filter(s => s.status === 'completed').length}
              </div>
            </div>
          </div>
        </div>

        {/* Search History List */}
        {filteredAndSortedSessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' ? 'No Search History Found' : `No ${filter} Sessions Found`}
            </h3>
            <p className="text-gray-600 mb-4">
              Execute your first intelligent search to see results here
            </p>
            <button
              onClick={() => navigate('/intelligent-search')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Start Intelligent Search
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-200">
                {filteredAndSortedSessions.map((session) => (
                  <div key={session.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-lg font-medium text-gray-900">
                            {session.search_area?.area_name || 'Unknown Area'}
                          </h3>
                          {getStatusBadge(session.status)}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                          <div className="bg-blue-50 rounded-lg p-3">
                            <div className="text-sm font-medium text-blue-900">Places Found</div>
                            <div className="text-2xl font-bold text-blue-600">{session.total_places_found || 0}</div>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-3">
                            <div className="text-sm font-medium text-purple-900">Circles Searched</div>
                            <div className="text-2xl font-bold text-purple-600">{session.circles_searched || 0}</div>
                            <div className="text-xs text-purple-700">of {session.user_approved_circles || 0} approved</div>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-3">
                            <div className="text-sm font-medium text-orange-900">API Requests</div>
                            <div className="text-2xl font-bold text-orange-600">{session.api_requests_made || 0}</div>
                            <div className="text-xs text-orange-700">Efficiency: {calculateEfficiency(session)} places/req</div>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3">
                            <div className="text-sm font-medium text-green-900">Duration</div>
                            <div className="text-2xl font-bold text-green-600">
                              {formatDuration(session.search_duration)}
                            </div>
                            {session.avg_places_per_circle && (
                              <div className="text-xs text-green-700">
                                Avg: {session.avg_places_per_circle.toFixed(1)} places/circle
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Started:</span> {new Date(session.created_at).toLocaleString()}
                          </div>
                          {session.completed_at && (
                            <div>
                              <span className="font-medium">Completed:</span> {new Date(session.completed_at).toLocaleString()}
                            </div>
                          )}
                        </div>

                        {session.ai_accuracy_score && (
                          <div className="mt-3 flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-2">AI Accuracy:</span>
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${session.ai_accuracy_score * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600">{(session.ai_accuracy_score * 100).toFixed(1)}%</span>
                          </div>
                        )}
                      </div>

                      <div className="ml-6 flex flex-col space-y-2">
                        <button
                          onClick={() => navigate('/intelligent-search', { 
                            state: { loadAreaId: session.search_area?.id } 
                          })}
                          className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                        >
                          View Area
                        </button>
                        {session.status === 'completed' && (
                          <button
                            onClick={() => navigate('/places', { 
                              state: { sessionId: session.id } 
                            })}
                            className="px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-md hover:bg-green-100 text-sm"
                          >
                            View Places
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} sessions
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-2 text-sm rounded-md ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Performance Summary */}
        {sessions.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {sessions.reduce((sum, s) => sum + (s.total_places_found || 0), 0)}
                </div>
                <div className="text-sm text-gray-500">Total Places Found</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {sessions.reduce((sum, s) => sum + (s.api_requests_made || 0), 0)}
                </div>
                <div className="text-sm text-gray-500">Total API Requests</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {sessions.filter(s => s.avg_places_per_circle).length > 0 ? (
                    sessions
                      .filter(s => s.avg_places_per_circle)
                      .reduce((sum, s) => sum + s.avg_places_per_circle, 0) / 
                    sessions.filter(s => s.avg_places_per_circle).length
                  ).toFixed(1) : '0'}
                </div>
                <div className="text-sm text-gray-500">Avg Places per Circle</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {sessions.filter(s => s.status === 'completed').length > 0 ? (
                    (sessions.filter(s => s.status === 'completed').length / sessions.length * 100).toFixed(0)
                  ) : '0'}%
                </div>
                <div className="text-sm text-gray-500">Success Rate</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntelligentSearchHistory;